import { Injectable } from '@angular/core';
import { Organization } from '@core/core';
import { isTrustedRedirectUrl, rememberCliReturn } from '@sdk/sdk';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class OrganizationRedirectHelper {
  getRedirectUrl(
    organizations: Organization[],
    redirect: string
  ): string | null {
    let url: URL;
    try {
      url = new URL(redirect);
    } catch {
      return null;
    }
    const orgId = this._getOrgId(url);

    const foundOrgs = organizations.filter(
      organization => organization.url.host === url.host
    );

    if (foundOrgs.length == 1 && !orgId) {
      const organization = foundOrgs[0];
      return this.getOrganizationUrl(organization);
    }

    // Only return the raw redirect when it targets one of the user's own
    // organization hosts (covers multi-org-per-host and pre-resolved /o/{id}/
    // deep links, including the local-dev dummy org) or a trusted *.sytex.io
    // host. Any other absolute URL is an open-redirect attempt and is rejected.
    if (foundOrgs.length > 0 || isTrustedRedirectUrl(redirect)) {
      return redirect;
    }

    return null;
  }

  getOrganizationUrl(organization: Organization): string {
    return `${organization.url}o/${organization.id}/`;
  }

  getOrganizationFromUrl(
    organizations: Organization[],
    url: string
  ): Organization | undefined {
    return organizations.find(organization =>
      url.startsWith(this.getOrganizationUrl(organization))
    );
  }

  /**
   * Signs the user in again through an organization's identity provider,
   * first passing `returnUrl` to {@link rememberCliReturn}: kept for the round
   * trip when it is a pending /cli request, a stale one cleared otherwise.
   */
  goToIdentityProvider(identityProviderName: string, returnUrl: string | null): void {
    rememberCliReturn(returnUrl);
    const identityProviderUrl = this._getIdentityProviderUrl(identityProviderName);
    this._cleanCognitoCookies();
    window.location.href = identityProviderUrl;
  }

  private _getIdentityProviderUrl(identityProviderName: string): string {
    const redirectLink = environment.sso.cognito_identity_provider_url;
    const redirectLinkWithClientId = redirectLink.replace('USERCLIENTID', environment.cognito.client_id);
    const redirectLinkWithOrganization = redirectLinkWithClientId.replace('USERINPUT', identityProviderName);
    // Force re-authentication so the external IdP always prompts for credentials
    return `${redirectLinkWithOrganization}&prompt=login`;
  }

  private _cleanCognitoCookies(): void {
    const domain = environment.cognito.cookie_domain;
    const cookies = document.cookie.split(';');
    cookies.forEach(cookie => {
      const name = cookie.split('=')[0].trim();
      if (name.startsWith('CognitoIdentityServiceProvider')) {
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${domain}; Secure; SameSite=Lax`;
      }
    });
  }

  private _getOrgId(url: URL): string {
    // Get organization id from url
    const r = new RegExp(/^\/o\/(\d*)\//);

    if (r.test(url.pathname)) {
      const matches = r.exec(url.pathname);
      return matches![1];
    }

    return '';
  }
}
